import React, { useEffect, useState } from "react";
import { Box, Divider, Grid, CircularProgress } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import PatientDatas from "src/modules/medcard/patient-datas";
import { useAppSelector } from "src/app/hooks";
import { mcGeneralDiseaseService, McGeneralDiseaseItem } from "src/api/medcards/mcGeneralDiseaseService";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import { useTranslate } from "src/locales";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { parseISO } from "date-fns";
import { toLocalISO } from "src/utils/date/dateHelper";

interface ExposuresModalProps {
  open: boolean;
  onClose: () => void;
  patientId?: string | number;
  editData?: McGeneralDiseaseItem | null;
}

const ExposuresModal: React.FC<ExposuresModalProps> = ({
  open,
  onClose,
  patientId,
  editData,
}) => {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => (state as any).auth);

  const [operationDate, setOperationDate] = useState<Date | null>(new Date());
  const [operationUser, setOperationUser] = useState<string>("");
  const [exposureType, setExposureType] = useState<number | null>(null);
  const [exposureTypeName, setExposureTypeName] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [note, setNote] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error">("success");
  const [loading, setLoading] = useState(false);

  // Kullanıcı adını otomatik olarak login durumundan al
  useEffect(() => {
    if (user?.username) {
      setOperationUser(user.username);
    }
  }, [user]);

  // Edit data varsa form'u doldur
  useEffect(() => {
    if (open && editData) {
      setExposureType(editData.exposuretype);
      setExposureTypeName(editData.name || "");
      setSelectedItem({
        id: editData.exposuretype,
        name: editData.name,
        displayField: editData.name,
      });
      setNote(editData.note || editData.privatenotes || "");
      if (editData.cuser) {
        setOperationUser(editData.cuser);
      }
      if (editData.pdate || editData.ctime) {
        try {
          const dateString = editData.pdate || editData.ctime;
          const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
          setOperationDate(date);
        } catch (e) {
          setOperationDate(new Date());
        }
      }
    }
  }, [open, editData]);

  // Modal açıldığında snackbar'ı temizle
  useEffect(() => {
    if (open) {
      setSnackbarOpen(false);
      setSnackbarMessage("");
    }
  }, [open]);

  // Modal kapandığında state'leri temizle
  useEffect(() => {
    if (!open) {
      setExposureType(null);
      setExposureTypeName("");
      setSelectedItem(null);
      setNote("");
      setOperationDate(new Date());
      setSnackbarOpen(false);
      setSnackbarMessage("");
    }
  }, [open]);

  const handleSave = async () => {
    if (!patientId) {
      setSnackbarMessage(t("exposure.errors.patient-id-not-found"));
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }

    // Edit modunda güncelleme
    if (editData) {
      if (!editData.id) {
        setSnackbarMessage(t("exposure.errors.id-not-found"));
        setSnackbarType("error");
        setSnackbarOpen(true);
        return;
      }

      if (exposureType === null || exposureType === undefined) {
        setSnackbarMessage(t("exposure.errors.exposure-type-not-selected"));
        setSnackbarType("error");
        setSnackbarOpen(true);
        return;
      }

      setLoading(true);
      try {
        await mcGeneralDiseaseService.updateExposure({
          id: editData.id,
          processdate: toLocalISO(operationDate),
          note: note || "",
          exposuretype: String(exposureType),
        });

        setSnackbarMessage(t("exposure.success.save-success"));
        setSnackbarType("success");
        setSnackbarOpen(true);
        setTimeout(() => {
          onClose();
        }, 1500);
      } catch (error: any) {
        console.error("Maruziyet güncelleme hatası:", error);
        const errorMessage = error?.response?.data?.error || error?.error || t("exposure.errors.save-error");
        setSnackbarMessage(errorMessage);
        setSnackbarType("error");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Yeni kayıt
    if (exposureType === null || exposureType === undefined) {
      setSnackbarMessage(t("exposure.errors.exposure-type-not-selected"));
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        ptid: Number(patientId),
        processid: exposureType, // Seçilen maruziyet tipi ID'si
        processdate: toLocalISO(operationDate),
        note: note || "",
        exposuretype: String(exposureType), // String olarak gönderilmeli
      };

      await mcGeneralDiseaseService.addExposure(requestData);
      setSnackbarMessage(t("exposure.success.save-success"));
      setSnackbarType("success");
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      console.error("Maruziyet kaydetme hatası:", error);
      const errorMessage = error?.response?.data?.error || error?.error || t("exposure.errors.save-error");
      setSnackbarMessage(errorMessage);
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          py: { xs: 2, sm: 3 },
          px: { xs: 1.5, sm: 2 },
          width: "100%",
          maxWidth: "100%",
        }}
      >
        {/* Başlık */}
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#f4f7fe",
            borderBottom: "1px solid #e0e0e0",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "flex-start",
            py: 1.5,
            px: 2.5,
            mb: 2,
          }}
        >
          <ByzMuiTypography isTitle={true} sx={{ fontSize: 20, lineHeight: "22px", color: "#111827" }}>
            {t("exposure.title")}
          </ByzMuiTypography>
        </Box>

        {/* Save / Close */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            gap: 1,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} disabled={loading} />
          <Box sx={{ flex: 1 }} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Hasta bilgisi kartı (grafiksiz) */}
        <Box sx={{ mb: 2 }}>
          <PatientDatas patientId={patientId} isCreateMedCard noMargin />
        </Box>

        {/* Form alanları */}
        <Box
          sx={{
            mt: 1,
            width: { xs: "100%", md: "60%" },
          }}
        >
          {/* Bölüm başlığı - Maruziyet */}
          <Box
            sx={{
              backgroundColor: "#F4F7FE",
              borderBottom: "1px solid #E0E0E0",
              mb: 1.5,

              py: 1,
            }}
          >
            <ByzMuiTypography sx={{ fontSize: 16 }}>
              {t("exposure.title")}
            </ByzMuiTypography>
          </Box>

          <Grid container spacing={0}>
            {/* İşlem Tarihi */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("exposure.operation-date")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiDateTimePicker
                    value={operationDate}
                    onChange={(val) => setOperationDate(val as Date | null)}
                    width="100%"
                    disabled
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* İşlem Yapan Kullanıcı */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("exposure.operation-user")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="İşlem Yapan Kullanıcı"
                    value={operationUser}
                    onChange={(e) => setOperationUser(e.target.value)}
                    fullWidth
                    cantChange
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Maruziyet Tipi (select) */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("exposure.exposure-type")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzSelectListPopupInput
                    label={t("buttons.chose") || "Seçiniz"}
                    placeholder={t("buttons.chose") || "Seçiniz"}
                    selectedItem={selectedItem}
                    disabled={!!editData}
                    singleSelection={true}
                    onChange={(value: any) => {
                      // Tek seçim için direkt item'ı al
                      const item = value;
                      if (item) {
                        setSelectedItem(item);
                        setExposureType(item?.id || item?.ID || null);
                        setExposureTypeName(item?.name || item?.NAME || "");
                      } else {
                        setSelectedItem(null);
                        setExposureType(null);
                        setExposureTypeName("");
                      }
                    }}
                    url="/reference/api/McGeneralDisease/field-exposure"
                    method="GET"
                    dialogTitle={t("exposure.dialog-title")}
                    displayField="name"
                    columns={[
                      { field: "id", headerName: "ID", width: 100 },
                      { field: "name", headerName: t("exposure.exposure-type"), flex: 1 },
                    ]}
                    dialogShowOkButton={true}
                    rowID={patientId}
                    showPatientData={false}
                    fullWidth
                    sx={{ width: "100%" }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Not */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("exposure.note")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="Giriniz"
                    placeholder={t("exposure.note") || "Not"}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      {/* Snackbar */}
      <ByzMuiSnackBar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        title={snackbarType === "success" ? t("common.success") : t("common.error")}
        message={snackbarMessage}
        type={snackbarType}
      />
    </OpenComponentDialog>
  );
};

export default ExposuresModal;
